import React from 'react';
import emailjs from '@emailjs/browser';
import { Link, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'motion/react';
import { Leaf, CheckCircle, AlertCircle, ChevronRight } from 'lucide-react';
import { LeafCluster } from '../../components/WoodlandSVG';
import { fadeUp, fadeLeft, fadeRight, pageTransition, viewportConfig } from '../../components/AnimationVariants';

export const REFERRAL_EMAIL = 'referral@wc4d.co.uk';

const EMAILJS_SERVICE_ID = 'service_rets9hq';
const EMAILJS_TEMPLATE_ID = 'template_kotn7as';
const EMAILJS_PUBLIC_KEY = '9V_E7cV1Z1OH9E5Lg';

export const REFERRAL_TYPES = [
  { label: 'CBCT', to: '/referral/cbct', desc: 'Cone Beam CT Scanning' },
  { label: 'Dental Implants', to: '/referral/dental-implants', desc: 'Implant placement & restoration' },
  { label: 'Extractions', to: '/referral/extractions', desc: 'Simple & surgical extractions' },
  { label: 'Endodontics', to: '/referral/endodontics', desc: 'Root canal treatment' },
  { label: 'Orthodontics', to: '/referral/orthodontics', desc: 'Braces & aligners' },
];

export const DENTIST_DEFAULTS = {
  dentistName: '', practiceName: '', practiceAddress1: '', practiceAddress2: '',
  practicePostcode: '', practicePhone: '', practiceEmail: '',
};
export const PATIENT_DEFAULTS = { patientName: '', patientPhone: '', patientEmail: '' };
export const FOOTER_DEFAULTS = { signature: '', privacyPolicy: false };

export function icls(field, errors, focused) {
  return `w-full rounded-xl border px-4 py-3 text-sm outline-none transition-all duration-200 bg-white ${
    errors[field] ? 'border-red-400' : focused === field ? 'border-forest ring-2 ring-forest/15' : 'border-cream-deeper'
  }`;
}

function buildClinicalDetails(data, specialty) {
  const lines = [];
  const add = (label, value) => { if (value) lines.push(`${label}: ${value}`); };
  switch (specialty) {
    case 'CBCT':
      add('Scan Reasons', data.scanReasons);
      add('Field of View', data.fieldOfView);
      add('Area of Interest', data.areaOfInterest);
      add('IRMER Compliant', data.irmer ? 'Yes' : 'No');
      add('Special Instructions', data.specialInstructions);
      break;
    case 'Endodontics':
      add('Tooth Numbers', data.toothNumbers);
      add('Medical History', data.medicalHistory);
      add('Reason for Referral', data.reasonForReferral);
      add('Current Symptoms', data.currentSymptoms);
      add('Symptom Duration', data.symptomDuration);
      add('Restorability', data.restorability);
      add('Restorability Notes', data.restorabilityNotes);
      break;
    case 'Extractions':
      add('Tooth Numbers', data.toothNumbers);
      add('Extraction Type', data.extractionType);
      add('Medical History', data.medicalHistory);
      add('Clinical Summary', data.clinicalSummary);
      add('Urgency', data.urgency);
      add('Special Instructions', data.specialInstructions);
      break;
    case 'Dental Implants':
      add('Tooth Sites', data.toothSites);
      add('Medical History', data.medicalHistory);
      add('Implant Scope', data.implantScope);
      add('Bone Quality', data.boneQuality);
      add('Prosthetic Plan', data.prostheticPlan);
      add('Existing Prosthetics', data.existingProsthetics);
      add('Radiographs Available', data.radiographsAvailable);
      break;
    case 'Orthodontics':
      add('Patient Type', data.patientType);
      add('Reason for Referral', data.reasonForReferral);
      add('Primary Concerns', data.primaryConcerns);
      add('Treatment Preference', data.treatmentPreference);
      add('Urgency', data.urgency);
      add('Special Instructions', data.specialInstructions);
      break;
    default: break;
  }
  return lines.join('\n');
}

export async function submitReferral(data, specialty) {
  const practiceAddress = [data.practiceAddress1, data.practiceAddress2, data.practicePostcode].filter(Boolean).join(', ');
  await emailjs.send(
    EMAILJS_SERVICE_ID,
    EMAILJS_TEMPLATE_ID,
    {
      specialty,
      dentist_name: data.dentistName,
      practice_name: data.practiceName,
      practice_address: practiceAddress,
      practice_phone: data.practicePhone,
      reply_to: data.practiceEmail,
      patient_name: data.patientName,
      patient_phone: data.patientPhone,
      patient_email: data.patientEmail,
      clinical_details: buildClinicalDetails(data, specialty),
    },
    EMAILJS_PUBLIC_KEY
  );
}

export function FieldError({ error }) {
  return (
    <AnimatePresence>
      {error && (
        <motion.p className="mt-1 text-xs text-red-500 flex items-center gap-1"
          initial={{ opacity: 0, y: -4 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }}>
          <AlertCircle className="w-3 h-3" />{error}
        </motion.p>
      )}
    </AnimatePresence>
  );
}

export function Fw({ children }) {
  return (
    <motion.div whileHover={{ y: -1 }} transition={{ type: 'spring', stiffness: 400 }}>
      {children}
    </motion.div>
  );
}

export function FormSection({ title, children }) {
  return (
    <div className="mb-8">
      <h3 className="font-serif text-forest font-semibold text-base mb-4 pb-2 border-b border-cream-deeper">{title}</h3>
      {children}
    </div>
  );
}

export function RadioGroup({ name, label, options, value, onChange, required, error }) {
  return (
    <div className="mb-1">
      {label && (
        <p className="text-sm font-medium text-gray-700 mb-2">
          {label} {required && <span className="text-red-400">*</span>}
        </p>
      )}
      <div className="flex flex-wrap gap-2">
        {options.map((opt) => (
          <label key={opt.value}
            className={`flex items-center gap-2 px-4 py-2 rounded-xl border text-sm cursor-pointer transition-all ${
              value === opt.value ? 'bg-forest text-white border-forest' : 'bg-white border-cream-deeper text-gray-600 hover:border-forest'
            }`}>
            <input type="radio" name={name} value={opt.value} checked={value === opt.value}
              onChange={onChange} className="sr-only" />
            {opt.label}
          </label>
        ))}
      </div>
      <FieldError error={error} />
    </div>
  );
}

export function CheckGroup({ name, label, options, values, onChange, required, error }) {
  return (
    <div className="mb-1">
      {label && (
        <p className="text-sm font-medium text-gray-700 mb-2">
          {label} {required && <span className="text-red-400">*</span>}
        </p>
      )}
      <div className="flex flex-wrap gap-2">
        {options.map((opt) => {
          const checked = values.includes(opt.value);
          return (
            <label key={opt.value}
              className={`flex items-center gap-2 px-4 py-2 rounded-xl border text-sm cursor-pointer transition-all ${
                checked ? 'bg-forest text-white border-forest' : 'bg-white border-cream-deeper text-gray-600 hover:border-forest'
              }`}>
              <input type="checkbox" name={name} value={opt.value} checked={checked}
                onChange={onChange} className="sr-only" />
              {opt.label}
            </label>
          );
        })}
      </div>
      <FieldError error={error} />
    </div>
  );
}

export function DentistSection({ form, errors, focused, onChange, onFocus, onBlur }) {
  const fields = [
    { id: 'dentistName', label: 'Referring Dentist', placeholder: 'Dr. Jane Smith', required: true },
    { id: 'practiceName', label: 'Practice Name', placeholder: 'Smith Dental Practice', required: true },
    { id: 'practiceAddress1', label: 'Address Line 1', placeholder: '123 High Street', required: true },
    { id: 'practiceAddress2', label: 'Address Line 2', placeholder: 'Town / City', required: false },
    { id: 'practicePostcode', label: 'Postcode', placeholder: 'AB12 3CD', required: true },
    { id: 'practicePhone', label: 'Practice Phone', type: 'tel', placeholder: '01234 567890', required: true },
    { id: 'practiceEmail', label: 'Practice Email', type: 'email', placeholder: 'reception@practice.com', required: true },
  ];
  return (
    <FormSection title="Referring Dentist Details">
      <div className="grid sm:grid-cols-2 gap-4">
        {fields.map((f) => (
          <Fw key={f.id}>
            <label className="block text-sm font-medium text-gray-700 mb-1.5" htmlFor={f.id}>
              {f.label} {f.required && <span className="text-red-400">*</span>}
            </label>
            <input id={f.id} name={f.id} type={f.type || 'text'} value={form[f.id]}
              onChange={onChange} placeholder={f.placeholder}
              onFocus={() => onFocus(f.id)} onBlur={onBlur}
              className={icls(f.id, errors, focused)} />
            <FieldError error={errors[f.id]} />
          </Fw>
        ))}
      </div>
    </FormSection>
  );
}

export function PatientSection({ form, errors, focused, onChange, onFocus, onBlur }) {
  return (
    <FormSection title="Patient Details">
      <div className="grid sm:grid-cols-3 gap-4">
        {[
          { id: 'patientName', label: 'Patient Name', placeholder: 'Full name', required: true },
          { id: 'patientPhone', label: 'Phone Number', type: 'tel', placeholder: '07700 900000', required: true },
          { id: 'patientEmail', label: 'Email Address', type: 'email', placeholder: 'patient@email.com', required: false },
        ].map((f) => (
          <Fw key={f.id}>
            <label className="block text-sm font-medium text-gray-700 mb-1.5" htmlFor={f.id}>
              {f.label} {f.required && <span className="text-red-400">*</span>}
            </label>
            <input id={f.id} name={f.id} type={f.type || 'text'} value={form[f.id]}
              onChange={onChange} placeholder={f.placeholder}
              onFocus={() => onFocus(f.id)} onBlur={onBlur}
              className={icls(f.id, errors, focused)} />
            <FieldError error={errors[f.id]} />
          </Fw>
        ))}
      </div>
    </FormSection>
  );
}

export function FormFooter({ form, errors, focused, onChange, onFocus, onBlur, submitting }) {
  const today = new Date().toLocaleDateString('en-GB');
  return (
    <FormSection title="Declaration">
      <div className="grid sm:grid-cols-2 gap-4 mb-5">
        <Fw>
          <label className="block text-sm font-medium text-gray-700 mb-1.5" htmlFor="signature">
            Signature (Full Name) <span className="text-red-400">*</span>
          </label>
          <input id="signature" name="signature" type="text" value={form.signature}
            onChange={onChange} placeholder="Type your full name"
            onFocus={() => onFocus('signature')} onBlur={onBlur}
            className={icls('signature', errors, focused)} />
          <FieldError error={errors.signature} />
        </Fw>
        <Fw>
          <label className="block text-sm font-medium text-gray-700 mb-1.5">Date</label>
          <input type="text" value={today} readOnly
            className="w-full rounded-xl border border-cream-deeper px-4 py-3 text-sm bg-cream text-gray-500 cursor-default" />
        </Fw>
      </div>
      <Fw>
        <label className="flex items-start gap-3 cursor-pointer">
          <input type="checkbox" name="privacyPolicy" checked={form.privacyPolicy} onChange={onChange}
            className="mt-0.5 w-4 h-4 accent-forest flex-shrink-0" />
          <span className="text-sm text-gray-600">
            I confirm I have obtained consent from the patient to share their information for the purposes of this referral, in accordance with GDPR and our{' '}
            <a href="/privacy" className="text-forest underline">Privacy Policy</a>.{' '}
            <span className="text-red-400">*</span>
          </span>
        </label>
        <FieldError error={errors.privacyPolicy} />
      </Fw>
      <motion.button type="submit" disabled={submitting}
        className="btn-primary w-full justify-center text-base py-3.5 mt-6 disabled:opacity-60 disabled:cursor-not-allowed"
        whileHover={{ scale: submitting ? 1 : 1.02, boxShadow: '0 8px 24px rgba(197,165,90,0.3)' }}
        whileTap={{ scale: submitting ? 1 : 0.98 }}>
        {submitting ? 'Submitting…' : 'Submit Referral'}
      </motion.button>
      <p className="text-xs text-gray-400 text-center mt-3">
        For urgent referrals, please also call us on{' '}
        <a href="tel:+442088665556" className="text-forest">020 8866 5556</a>.
      </p>
    </FormSection>
  );
}

export function SuccessScreen({ patientName, onReset }) {
  return (
    <motion.div className="text-center py-16"
      initial={{ opacity: 0, scale: 0.9 }} animate={{ opacity: 1, scale: 1, transition: { type: 'spring', stiffness: 300 } }}
      exit={{ opacity: 0 }}>
      <motion.div className="w-20 h-20 rounded-full bg-sage-pale flex items-center justify-center mx-auto mb-5"
        initial={{ scale: 0 }} animate={{ scale: 1 }}
        transition={{ type: 'spring', stiffness: 400, damping: 15, delay: 0.1 }}>
        <motion.div initial={{ scale: 0 }} animate={{ scale: 1 }} transition={{ delay: 0.3, type: 'spring' }}>
          <CheckCircle className="w-10 h-10 text-forest" />
        </motion.div>
      </motion.div>
      <motion.h3 className="font-serif text-forest text-2xl font-semibold mb-2"
        initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.4 }}>
        Referral Submitted!
      </motion.h3>
      <motion.p className="text-gray-500 leading-relaxed max-w-sm mx-auto mb-2"
        initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.5 }}>
        Thank you for referring <strong className="text-forest">{patientName}</strong>. We will be in touch shortly to arrange their appointment.
      </motion.p>
      <motion.p className="text-xs text-gray-400 max-w-sm mx-auto mb-8"
        initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.55 }}>
        A confirmation will be sent to your practice email address.
      </motion.p>
      <motion.button className="btn-outline text-sm" onClick={onReset}
        initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.7 }}>
        Submit Another Referral
      </motion.button>
    </motion.div>
  );
}

export function ReferralSidebar() {
  const { pathname } = useLocation();
  return (
    <motion.div className="bg-white rounded-2xl p-6 shadow-sm border border-cream-deeper lg:sticky lg:top-28"
      variants={fadeRight} initial="hidden" whileInView="visible" viewport={viewportConfig}>
      <h3 className="font-serif text-forest text-lg font-semibold mb-4">Referral Types</h3>
      <div className="space-y-1">
        {REFERRAL_TYPES.map((type) => {
          const active = pathname === type.to;
          return (
            <Link key={type.to} to={type.to}
              className={`flex items-center justify-between px-4 py-3 rounded-xl text-sm transition-all ${
                active ? 'bg-forest text-white' : 'text-gray-600 hover:bg-sage-pale hover:text-forest'
              }`}>
              <div>
                <div className="font-semibold">{type.label}</div>
                <div className={`text-xs ${active ? 'text-cream/70' : 'text-gray-400'}`}>{type.desc}</div>
              </div>
              <ChevronRight className={`w-4 h-4 flex-shrink-0 ${active ? 'text-white' : 'text-gray-300'}`} />
            </Link>
          );
        })}
      </div>
      <div className="mt-5 p-4 bg-sage-pale rounded-xl">
        <p className="text-xs text-forest/80 leading-relaxed">
          <strong>Questions about referring?</strong><br />
          Call <a href="tel:+442088665556" className="underline">020 8866 5556</a> or email{' '}
          <a href={`mailto:${REFERRAL_EMAIL}`} className="underline break-all">{REFERRAL_EMAIL}</a>
        </p>
      </div>
    </motion.div>
  );
}

export function ReferralPageWrapper({ title, subtitle, photo, children }) {
  return (
    <motion.div {...pageTransition} className="pt-24">
      {/* Hero */}
      <section className="relative py-20 px-4 overflow-hidden">
        {photo && (
          <div className="absolute inset-0">
            <img src={photo} alt="" className="w-full h-full object-cover" aria-hidden="true" />
            <div className="absolute inset-0 bg-forest-dark/80" />
          </div>
        )}
        {!photo && <div className="absolute inset-0 bg-gradient-to-br from-forest-dark to-forest" />}
        <motion.div className="absolute right-0 top-0 w-64 opacity-15 hidden md:block"
          animate={{ y: [0, -10, 0] }} transition={{ duration: 7, repeat: Infinity, ease: 'easeInOut' }}>
          <LeafCluster />
        </motion.div>
        <div className="relative z-10 max-w-3xl mx-auto text-center">
          <motion.p className="text-sage-light text-sm uppercase tracking-widest font-medium mb-4 flex items-center justify-center gap-2"
            variants={fadeUp} initial="hidden" animate="visible" transition={{ delay: 0.1 }}>
            <Leaf className="w-4 h-4" /> Specialist Referrals
          </motion.p>
          <motion.h1 className="text-4xl md:text-5xl font-serif text-white font-semibold mb-5"
            variants={fadeUp} initial="hidden" animate="visible" transition={{ delay: 0.2 }}>
            {title}
          </motion.h1>
          <motion.p className="text-cream/75 text-lg leading-relaxed"
            variants={fadeUp} initial="hidden" animate="visible" transition={{ delay: 0.3 }}>
            {subtitle}
          </motion.p>
        </div>
      </section>

      {/* Content */}
      <section className="section-padding bg-cream">
        <div className="container-max">
          <div className="grid lg:grid-cols-4 gap-8">
            <div className="lg:col-span-1">
              <ReferralSidebar />
            </div>
            <motion.div className="lg:col-span-3" variants={fadeLeft} initial="hidden" whileInView="visible" viewport={viewportConfig}>
              <div className="bg-white rounded-3xl p-8 shadow-sm border border-cream-deeper">
                {children}
              </div>
            </motion.div>
          </div>
        </div>
      </section>
    </motion.div>
  );
}

export function validateCommon(form) {
  const errors = {};
  if (!form.dentistName.trim()) errors.dentistName = 'Required';
  if (!form.practiceName.trim()) errors.practiceName = 'Required';
  if (!form.practiceAddress1.trim()) errors.practiceAddress1 = 'Required';
  if (!form.practicePostcode.trim()) errors.practicePostcode = 'Required';
  if (!form.practicePhone.trim()) errors.practicePhone = 'Required';
  if (!form.practiceEmail.trim()) errors.practiceEmail = 'Required';
  else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.practiceEmail)) errors.practiceEmail = 'Invalid email';
  if (!form.patientName.trim()) errors.patientName = 'Required';
  if (!form.patientPhone.trim()) errors.patientPhone = 'Required';
  if (!form.signature.trim()) errors.signature = 'Required';
  if (!form.privacyPolicy) errors.privacyPolicy = 'You must confirm consent before submitting';
  return errors;
}
